/**
 * Focus Drive — 100-level survival campaign across 7 worlds.
 *
 * REVISED (2026-02-10): Time-based survival, not distance-based.
 *   • Every level has a countdown timer (duration in seconds).
 *   • Stay on the road until the timer expires → level cleared.
 *   • Focus Tokens remain — collecting them is optional and rewards stars,
 *     Focus Points, Garage unlock progress, and achievement progress.
 *
 * Star rules (uniform):
 *   ⭐   Survive the timer.
 *   ⭐⭐  Survive + collect ≥ 50% of the level's token ceiling.
 *   ⭐⭐⭐ Survive + collect ≥ 90% of tokens + zero near-misses.
 */

export const WORLDS = [
  { id: 1, name: 'City Nights',    range: [1, 15],   color: '#0B192C', accent: '#C5A059', tagline: 'Where the road first quiets.' },
  { id: 2, name: 'Neon Highway',   range: [16, 30],  color: '#0F1A2E', accent: '#8FB8FF', tagline: 'Signal cuts through noise.'    },
  { id: 3, name: 'Sunset Coast',   range: [31, 45],  color: '#2A1B2E', accent: '#FFB07A', tagline: 'Golden hour, open air.'         },
  { id: 4, name: 'Storm Drive',    range: [46, 60],  color: '#0A0F1C', accent: '#7DB4FF', tagline: 'Steady hands in the weather.'   },
  { id: 5, name: 'Mountain Pass',  range: [61, 75],  color: '#1A2A1E', accent: '#A8E080', tagline: 'Cold air, clean line.'          },
  { id: 6, name: 'Desert Run',     range: [76, 90],  color: '#2A1F0F', accent: '#F3C978', tagline: 'Nothing but you and the road.'  },
  { id: 7, name: 'Aurora Highway', range: [91, 100], color: '#0B1E29', accent: '#7CE7C1', tagline: 'The road above the clouds.'     },
];

export function worldForLevel(level) {
  return WORLDS.find(w => level >= w.range[0] && level <= w.range[1]) || WORLDS[0];
}

const lerp = (a, b, t) => a + (b - a) * t;

/** Level factory — deterministic, parameter-driven survival levels. */
function buildLevel(n) {
  const w = worldForLevel(n);
  const inWorldT = (n - w.range[0]) / Math.max(1, (w.range[1] - w.range[0]));
  const overallT = (n - 1) / 99;

  // Survival duration ramps across the campaign
  //   World 1: 20-30s   →   World 7: 45-60s
  const worldDurations = [
    [22, 30], [26, 36], [30, 40], [32, 44], [36, 50], [40, 55], [45, 60],
  ];
  const [dMin, dMax] = worldDurations[w.id - 1];
  const duration = Math.round(dMin + (dMax - dMin) * inWorldT);

  // Physics knobs — scale gently across campaign
  const baseSpeed = Math.round(lerp(180, 380, overallT));
  const maxSpeed  = Math.round(lerp(240, 520, overallT));
  const obsRate   = lerp(1.9, 0.75, overallT);
  const tokRate   = lerp(1.2, 0.85, overallT);
  const ramp      = lerp(0.02, 0.055, overallT);
  const curveAmp  = lerp(10, 60, overallT);
  const curveFreq = lerp(0.16, 0.34, overallT);
  const doubleObsProb = lerp(0.02, 0.32, overallT);

  // Token ceiling for star thresholds (never gates progression)
  const tokenMax = Math.max(3, Math.round(duration / tokRate * 0.7));

  // Weather cosmetic (varies within each world for interest)
  const weather = w.id === 1
    ? (inWorldT < 0.5 ? 'clear' : 'dusk')
    : w.id === 2
      ? (inWorldT < 0.5 ? 'neon' : 'rain')
      : w.id === 3
        ? (inWorldT < 0.5 ? 'sunset' : 'coast')
        : w.id === 4
          ? (inWorldT < 0.5 ? 'storm' : 'rain-heavy')
          : w.id === 5
            ? (inWorldT < 0.5 ? 'mountain' : 'snow')
            : w.id === 6
              ? (inWorldT < 0.5 ? 'desert' : 'dust')
              : (inWorldT < 0.5 ? 'aurora' : 'aurora-deep');

  return {
    id: n,
    world: w.id,
    name: `Level ${n}`,
    duration,
    tokenMax,
    starThresholds: {
      one:   0,
      two:   Math.max(1, Math.ceil(tokenMax * 0.5)),
      three: Math.max(2, Math.ceil(tokenMax * 0.9)),
    },
    baseSpeed, maxSpeed, obsRate, tokRate, ramp, curveAmp, curveFreq, doubleObsProb,
    weather,
    isLongRun: n % 10 === 0,
  };
}

export const FD_LEVELS = Array.from({ length: 100 }, (_, i) => buildLevel(i + 1));
export const FD_MAX_LEVEL = 100;

export function getFDLevelConfig(n) {
  return FD_LEVELS[Math.max(0, Math.min(99, n - 1))];
}

/** Award stars based on a survival run. */
export function computeFDStars(level, run) {
  const survived = !!run.survived;
  if (!survived) return 0;
  const tokens = run.tokens ?? 0;
  const nearMisses = run.nearMisses ?? 0;
  if (tokens >= level.starThresholds.three && nearMisses === 0) return 3;
  if (tokens >= level.starThresholds.two) return 2;
  return 1;
}

/* -------------------- Garage: skins + trails -------------------- */
export const GARAGE_SKINS = [
  { id: 'gold',    name: 'Signature Gold',   unlockLevel: 1,  color: '#C5A059', accent: '#E1C88A', desc: 'The classic Brotherhood run.' },
  { id: 'chrome',  name: 'Chrome Focus',     unlockLevel: 5,  color: '#D8DDE5', accent: '#F5F7FA', desc: 'Cool, mirrored, hard to rattle.' },
  { id: 'jade',    name: 'Jade Line',        unlockLevel: 10, color: '#7CE7C1', accent: '#B4F0DA', desc: 'Steady like a mountain stream.' },
  { id: 'aurora',  name: 'Aurora',           unlockLevel: 20, color: '#8FB8FF', accent: '#C3D9FF', desc: 'Above the storm, below the sky.' },
];

export const GARAGE_TRAILS = [
  { id: 'none',    name: 'None',             unlockLevel: 1,  color: 'transparent', desc: 'Clean run, no trail.' },
  { id: 'ember',   name: 'Ember Trail',      unlockLevel: 5,  color: '#F3C978',     desc: 'A quiet warmth behind you.' },
  { id: 'flow',    name: 'Flow Trail',       unlockLevel: 10, color: '#7CE7C1',     desc: 'Green line of focus.' },
  { id: 'aurora',  name: 'Aurora Trail',     unlockLevel: 20, color: '#8FB8FF',     desc: 'Long, cool, high-altitude.' },
];

export function skinsUnlockedAtLevel(highestUnlocked) {
  return GARAGE_SKINS.filter(s => s.unlockLevel <= highestUnlocked).map(s => s.id);
}
export function trailsUnlockedAtLevel(highestUnlocked) {
  return GARAGE_TRAILS.filter(t => t.unlockLevel <= highestUnlocked).map(t => t.id);
}
export function skinById(id) { return GARAGE_SKINS.find(s => s.id === id) || GARAGE_SKINS[0]; }
export function trailById(id) { return GARAGE_TRAILS.find(t => t.id === id) || GARAGE_TRAILS[0]; }

export const MASTER_UNLOCK_LEVEL = 100;
